import type { DispatchTime, DispatchInfo } from 'root/types/businessTypes';
import { BusinessOpenStatus, SchedulingType, DispatchType } from 'root/types/businessTypes';
import { makeObservable, computed, action, toJS, observable, configure } from 'mobx';
import { dispatchState } from './DispatchState';

configure({ isolateGlobalState: true });

export class HeaderStore {
  _isMultiLocation: boolean = false;
  currentTimezone: string;
  constructor(private timezone: string, private locale: string) {
    makeObservable(this, {
      _isMultiLocation: observable,
      currentTimezone: observable,
      dispatchType: computed,
      openStatus: computed,
      selectedAddress: computed,
      selectedTime: computed,
      asapTimeExact: computed,
      asapTimeRange: computed,
      minOrder: computed,
      deliveryFee: computed,
      schedulingType: computed,
      isASAP: computed,
      isDeliveryAvailable: computed,
      isPickupAvailable: computed,
      isDeliveryConfigured: computed,
      isPickupConfigured: computed,
      hasConfiguredDispatches: computed,
      hasAvailableDispatches: computed,
      pickupAddress: computed,
      hasDispatchDetails: computed,
      dispatchState: computed,
      isLoading: computed,
      isDataLoaded: computed,
      setIsMultiLocation: action,
      updateDispatchState: action,
      syncTimezone: action,
    });
    this.currentTimezone = dispatchState.timezone || timezone;
  }

  syncTimezone() {
    this.currentTimezone = dispatchState.timezone || this.timezone;
  }

  get operation() {
    return dispatchState.currentOperation;
  }

  get location() {
    return this.operation?.locationDetails;
  }

  get isMultiLocation() {
    return this._isMultiLocation;
  }

  get isLocationSelected() {
    return !!dispatchState.isLocationSelected && this.isMultiLocation && !!this.operation?.id;
  }

  setIsMultiLocation(isMultiLocation: boolean) {
    this._isMultiLocation = isMultiLocation;
  }

  get isLoading() {
    return !!dispatchState.isLoading;
  }

  get isDataLoaded() {
    return !!dispatchState.isDataLoaded;
  }

  get dispatchType() {
    return dispatchState.selectedDispatchType;
  }

  get openStatus() {
    return this.operation?.operationType !== 'PRE_ORDER' || this.hasAvailableDispatches
      ? BusinessOpenStatus.Open
      : BusinessOpenStatus.Closed;
  }

  get isOpen() {
    return this.openStatus === BusinessOpenStatus.Open;
  }

  get isDelivery() {
    return this.dispatchType === DispatchType.DELIVERY;
  }

  get isPickup() {
    return this.dispatchType === DispatchType.PICKUP;
  }

  get selectedAddress() {
    const address = toJS(dispatchState.dispatchInfo.address);
    if (this.isPickup && this.isMultiLocation && !address) {
      return this.operation?.locationDetails?.address;
    }
    return address;
  }

  get selectedTime(): DispatchTime | undefined {
    const { selectedTimeSlot: { startsNow, startTime, endTime } = {} } = dispatchState.dispatchInfo;
    const haveSelectedTime = !startsNow && !!(startTime && endTime);
    if (haveSelectedTime) {
      return {
        from: startTime.setZone(this.currentTimezone).setLocale(this.locale),
        until: endTime.setZone(this.currentTimezone).setLocale(this.locale),
      };
    }
    return undefined;
  }

  get asapTimeExact() {
    const { address, selectedTimeSlot, maxTimeOptions } = dispatchState.dispatchInfo;
    if (address && selectedTimeSlot?.startsNow) {
      return selectedTimeSlot.fulfillmentDetails.maxTimeOptions;
    }
    return this.schedulingType !== SchedulingType.PRE_ORDER ? maxTimeOptions : undefined;
  }

  get asapTimeRange() {
    const { address, selectedTimeSlot, durationRangeOptions } = dispatchState.dispatchInfo;
    if (address && selectedTimeSlot?.startsNow) {
      return selectedTimeSlot.fulfillmentDetails.durationRangeOptions;
    }
    return this.schedulingType !== SchedulingType.PRE_ORDER ? durationRangeOptions : undefined;
  }

  get minOrder() {
    const { address, selectedTimeSlot, minOrder } = dispatchState.dispatchInfo;
    if (address && selectedTimeSlot) {
      return selectedTimeSlot.fulfillmentDetails?.minOrderPrice ?? undefined;
    }
    return minOrder;
  }

  get deliveryFee() {
    if (!this.isDelivery) {
      return undefined;
    }

    const { selectedTimeSlot, deliveryFee, address } = dispatchState.dispatchInfo;
    if (address && selectedTimeSlot) {
      return selectedTimeSlot.fulfillmentDetails?.fee ?? undefined;
    }
    return deliveryFee;
  }

  get freeDispatchPriceThreshold() {
    const { address, freeFulfillmentPriceThreshold, selectedTimeSlot } = dispatchState.dispatchInfo;
    if (address && selectedTimeSlot) {
      return selectedTimeSlot.fulfillmentDetails?.freeFulfillmentPriceThreshold ?? undefined;
    }
    return freeFulfillmentPriceThreshold;
  }

  get schedulingType(): SchedulingType {
    if (this.operation?.operationType === 'ASAP') {
      return this.operation.allowAsapFutureHandling
        ? SchedulingType.ASAP_AND_FUTURE
        : SchedulingType.ASAP;
    }
    return SchedulingType.PRE_ORDER;
  }

  get isASAP() {
    return this.schedulingType === 'ASAP';
  }

  get shouldShowTimeInfo() {
    return !this.isLoading && this.isCurrentDispatchAvailable;
  }

  get shouldShowAddressChange() {
    return !this.isLoading && (this.isDelivery || this.isMultiLocation);
  }

  get startsNow() {
    return !!(this.isASAP || dispatchState.dispatchInfo.selectedTimeSlot?.startsNow);
  }

  get isCurrentDispatchAvailable() {
    return dispatchState.isDispatchAvailable(this.dispatchType, true);
  }

  get isPickupAvailable() {
    return dispatchState.isDispatchAvailable(DispatchType.PICKUP);
  }

  get isDeliveryAvailable() {
    return dispatchState.isDispatchAvailable(DispatchType.DELIVERY);
  }

  get isPickupConfigured() {
    return dispatchState.isDispatchConfigured(DispatchType.PICKUP);
  }

  get isDeliveryConfigured() {
    return dispatchState.isDispatchConfigured(DispatchType.DELIVERY);
  }

  get hasConfiguredDispatches() {
    return dispatchState.hasConfiguredDispatches;
  }

  get hasAvailableDispatches() {
    return dispatchState.hasAvailableDispatches;
  }

  get availableDispatchTypesForCurrentLocation() {
    return dispatchState.availableDispatchTypesForCurrentLocation;
  }

  get configuredDispatchTypesForCurrentLocations() {
    return dispatchState.configuredDispatchTypesForCurrentLocations;
  }

  get pickupAddress() {
    return toJS(dispatchState.dispatchesInfo[DispatchType.PICKUP]?.address);
  }

  get onlyDeliveryConfiguredForCurrentLocation() {
    return (
      this.configuredDispatchTypesForCurrentLocations.includes(DispatchType.DELIVERY) &&
      !this.configuredDispatchTypesForCurrentLocations.includes(DispatchType.PICKUP)
    );
  }

  get onlyDeliveryConfigured() {
    return this.isDeliveryConfigured && !this.isPickupConfigured;
  }

  get hasDispatchDetails() {
    return (
      (this.isASAP && (typeof this.asapTimeExact === 'number' || !!this.asapTimeRange)) ||
      !!this.selectedTime ||
      this.isDelivery ||
      !!this.selectedAddress
    );
  }

  get hasDispatchTime() {
    return (
      (this.startsNow && (typeof this.asapTimeExact === 'number' || !!this.asapTimeRange)) ||
      !!this.selectedTime ||
      this.isDelivery
    );
  }

  get dispatchState() {
    return toJS(dispatchState.state);
  }

  updateDispatchState(dispatchType: DispatchType, dispatchInfo: DispatchInfo) {
    dispatchState.update(dispatchType, dispatchInfo);
  }
}
